import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { Group, Flex, Pill, Radio, Stack, Switch, Text, Divider } from '@mantine/core';
import { FormattedMessage } from 'react-intl';

import DataDrivenLogo from '../../../../../../../../../components/data-driven-logo';
import { HelpArticleLink } from '../../../../../../../../../components/help-article-link';
import PackageDropdown from '../../../../../../../../../components/package-dropdown/container';
import ShippingServiceSelector from '../../../../../../../../../components/shipping-service-selector';
import { linkIDs } from '../../../../../../../../../services/link-service/links';

import classes from './customer-picks-service.module.less';

import messages from './messages';

const dangerousGoodsLink = (chunks: ReactNode) => <HelpArticleLink articleId={linkIDs.UpsDangerousGoods}>{chunks}</HelpArticleLink>;

const CUSTOMER_PICKS = 'customerPicks';

type CustomerPicksServiceProps = {
  isSelected: boolean;
  onOptionChange: () => void;
  onServiceSelected: () => void;
  selectedPortalServiceIds: number[];
  serviceNameById: Map<string, string>;
  carrierIdByServiceId: Map<string, string>;
  onRemovePortalService: (serviceId: number) => () => void;
  carrierGroups: { groupId: string; name: { id: string; defaultMessage: string } }[];
  isNoBoxNoLabelEnabled: boolean;
  hasNoBoxNoLabelService: boolean;
  onNoBoxNoLabelChange: (isChecked: boolean) => void;
  selectedPackageKey?: string;
  onPackageSelected: (packageType: { packageTypeId: string; carrierId?: string } | undefined) => void;
  packageFilter: (pkg: Record<string, unknown>) => boolean;
};

export const CustomerPicksService = ({
  isSelected,
  onOptionChange,
  onServiceSelected,
  selectedPortalServiceIds,
  serviceNameById,
  carrierIdByServiceId,
  onRemovePortalService,
  carrierGroups,
  isNoBoxNoLabelEnabled,
  hasNoBoxNoLabelService,
  onNoBoxNoLabelChange,
  selectedPackageKey,
  onPackageSelected,
  packageFilter,
}: CustomerPicksServiceProps) => {
  const carrierNameByCarrierId = useMemo(() => {
    const map = new Map<string, { id: string; defaultMessage: string }>();
    carrierGroups.forEach((group) => {
      map.set(group.groupId, group.name);
    });
    return map;
  }, [carrierGroups]);

  const serviceIdsByCarrierId = useMemo(() => {
    const map = new Map<string, number[]>();
    selectedPortalServiceIds.forEach((serviceId) => {
      const carrierId = carrierIdByServiceId.get(serviceId.toString()) ?? 'unknown';
      const existing = map.get(carrierId) ?? [];
      map.set(carrierId, [...existing, serviceId]);
    });
    return map;
  }, [selectedPortalServiceIds, carrierIdByServiceId]);

  return (
    <Flex align='flex-start' gap='md'>
      <Radio onClick={onOptionChange} value={CUSTOMER_PICKS} />
      <Stack>
        <div>
          <Text size='sm'>
            <FormattedMessage {...messages.customerPicksTheService} />
          </Text>
          <Text c='gray.6' size='xs'>
            <FormattedMessage {...messages.customerPicksTheServiceDescription} />
          </Text>
        </div>

        {isSelected && (
          <>
            <div>
              <Text fw={500} size='sm'>
                <FormattedMessage {...messages.availableServices} />
              </Text>
              <ShippingServiceSelector
                defaultEmptyText={messages.selectAService}
                onSelected={onServiceSelected}
                selectedItemName=''
                serviceGroups={carrierGroups}
              />
            </div>

            {selectedPortalServiceIds.length > 0 && (
              <Stack gap='xs'>
                {[...serviceIdsByCarrierId.entries()].map(([carrierId, serviceIds]) => (
                  <Stack key={carrierId} gap='xs'>
                    <Text fw={500} size='sm'>
                      <FormattedMessage
                        {...(carrierNameByCarrierId.get(carrierId) ?? { id: 'carrier.unknown', defaultMessage: carrierId })}
                      />
                    </Text>
                    <Group gap='xs'>
                      {serviceIds.map((serviceId) => (
                        <Pill key={serviceId} onRemove={onRemovePortalService(serviceId)} withRemoveButton>
                          <Group gap={4} wrap='nowrap'>
                            <div className={classes['carrier-logo']}>
                              <DataDrivenLogo carrierId={carrierIdByServiceId.get(serviceId.toString())} logoType='icon' />
                            </div>
                            {serviceNameById.get(serviceId.toString()) ?? serviceId.toString()}
                          </Group>
                        </Pill>
                      ))}
                    </Group>
                  </Stack>
                ))}
              </Stack>
            )}

            <div className={classes['package-dropdown']}>
              <Text fw={500} size='sm'>
                <FormattedMessage {...messages.package} />
              </Text>
              <PackageDropdown
                className={classes['package-dropdown']}
                defaultEmptyText={<FormattedMessage {...messages.sameAsOutbound} />}
                dropdownMenuShouldHaveWidthOfButton
                onPackageSelected={onPackageSelected}
                packageFilter={packageFilter}
                selectedPackageKey={selectedPackageKey}
                showEmptyOption
                simpleToggler
                variant='form'
              />
            </div>

            {hasNoBoxNoLabelService && (
              <>
                <Divider my='md' />

                <Flex align='center' justify='space-between'>
                  <div>
                    <Text size='sm'>
                      <FormattedMessage {...messages.noBoxNoLabelTitle} />
                    </Text>
                    <Text c='gray.6' maw={448} size='xs'>
                      <FormattedMessage {...messages.noBoxNoLabelDescription} values={{ dangerousGoodsLink }} />
                    </Text>
                  </div>
                  <Switch checked={isNoBoxNoLabelEnabled} onChange={(event) => onNoBoxNoLabelChange(event.currentTarget.checked)} />
                </Flex>
              </>
            )}
          </>
        )}
      </Stack>
    </Flex>
  );
};
